import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUp, MessageSquareText, Search, X } from "lucide-react";

import { LOCATIONS } from "../PayrollItems/data";
import AssignEmployeesPanel from "../../components/AssignEmployeesPanel";

import {
  BADGE_PRIMARY,
  BG_CARD,
  BG_PAGE,
  BORDER,
  BORDER_LIGHT,
  BTN_GHOST,
  BTN_OUT,
  BTN_TEAL,
  BTN_TEAL_OUT,
  ICON_BTN,
  LINK,
  TEXT_INK,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_TERTIARY,
} from "./styles";
import { employeeName, shortAddress } from "./helpers";
import { useEmployees } from "./useEmployees";

// AssignEmployeesPanel is still a .js file with stricter inferred prop types.
const AssignPanel = AssignEmployeesPanel as any;

export default function WorkLocation() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = LOCATIONS.find((l) => l.id === id);

  const [search, setSearch] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);

  const {
    data: employees = [],
    isLoading: loading,
    error,
  } = useEmployees(id, !!location && !!location.isPrimary);

  const goBack = () => navigate("/payroll/items");
  const openAssign = () => setAssignOpen(true);

  const sorted = useMemo(() => {
    return [...employees]
      .filter(
        (e) =>
          !search || employeeName(e).toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        const al = (a.last_name || "").toLowerCase();
        const bl = (b.last_name || "").toLowerCase();
        return al < bl ? -1 : al > bl ? 1 : 0;
      });
  }, [employees, search]);

  if (!location) {
    return (
      <div
        style={{
          background: BG_CARD,
          minHeight: "100vh",
          width: "100%",
          padding: 40,
          textAlign: "center",
          color: TEXT_SECONDARY,
          fontFamily: "inherit",
        }}
      >
        Work location not found.{" "}
        <span style={LINK} onClick={goBack}>
          Back to Payroll items
        </span>
      </div>
    );
  }

  const eligible = location.isPrimary ? [] : employees;
  const errMsg = error instanceof Error ? error.message : null;
  const addressShort = shortAddress(location.address);

  return (
    <div style={{ background: BG_CARD, minHeight: "100vh", width: "100%", fontFamily: "inherit" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 28px",
          borderBottom: "0.5px solid " + BORDER,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={ICON_BTN} onClick={goBack}>
            <ArrowLeft size={18} />
          </span>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_INK,
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            Work location
          </h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            onClick={() => alert("Feedback form coming next")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 500,
              color: TEXT_SECONDARY,
              padding: "7px 11px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            <MessageSquareText size={16} />
            How can we improve?
          </span>
          <span style={ICON_BTN} onClick={goBack}>
            <X size={18} />
          </span>
        </div>
      </div>

      <div
        style={{
          padding: "24px 28px 24px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          {location.isPrimary && (
            <div style={{ marginBottom: 8 }}>
              <span style={BADGE_PRIMARY}>PRIMARY</span>
            </div>
          )}
          <div style={{ fontSize: 13, color: TEXT_TERTIARY }}>{location.address}</div>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: TEXT_INK,
              margin: "3px 0 0",
              letterSpacing: "-0.01em",
            }}
          >
            {location.name}
          </h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span style={LINK} onClick={() => alert("Edit work location coming next")}>
            Edit
          </span>
          <button style={BTN_TEAL_OUT} onClick={openAssign}>
            Assign employee(s)
          </button>
        </div>
      </div>

      <div
        style={{
          padding: "0 28px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            border: "0.5px solid " + BORDER,
            borderRadius: 9,
            padding: "9px 13px",
            fontSize: 14,
            flex: 1,
            maxWidth: 360,
          }}
        >
          <Search size={16} style={{ color: TEXT_TERTIARY }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontFamily: "inherit",
              fontSize: 14,
              color: TEXT_PRIMARY,
              background: "transparent",
            }}
          />
        </div>
        <button style={BTN_OUT} onClick={() => alert("Filters coming next")}>
          Filters
        </button>
      </div>

      <div style={{ padding: "0 28px 24px" }}>
        <div
          style={{
            padding: "12px 4px 10px",
            borderBottom: "0.5px solid " + BORDER,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: TEXT_INK,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Employee ({sorted.length})
            <ArrowUp size={14} style={{ color: TEXT_TERTIARY }} />
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: TEXT_TERTIARY,
            }}
          >
            ACTION
          </div>
        </div>
        {loading ? (
          <div
            style={{
              padding: 30,
              textAlign: "center",
              color: TEXT_SECONDARY,
              fontSize: 13,
            }}
          >
            Loading employees...
          </div>
        ) : errMsg ? (
          <div
            style={{
              padding: 12,
              background: "#FEE2E2",
              border: "0.5px solid #F87171",
              borderRadius: 8,
              color: "#991B1B",
              fontSize: 13,
              marginTop: 12,
            }}
          >
            <strong>Could not load:</strong> {errMsg}
          </div>
        ) : sorted.length === 0 ? (
          <div
            style={{
              border: "1px dashed " + BORDER,
              borderRadius: 10,
              padding: 22,
              color: TEXT_SECONDARY,
              fontSize: 14,
              background: BG_PAGE,
              marginTop: 12,
            }}
          >
            No employees at this location yet.{" "}
            <span style={LINK} onClick={openAssign}>
              Assign employees
            </span>
          </div>
        ) : (
          sorted.map((emp) => (
            <div
              key={String(emp.id)}
              style={{
                padding: "14px 4px",
                borderBottom: "0.5px solid " + BORDER_LIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 14, color: TEXT_PRIMARY }}>{employeeName(emp)}</div>
                <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 2 }}>
                  {addressShort}
                </div>
              </div>
              <span
                style={LINK}
                onClick={() => alert("Update tax withholding coming next")}
              >
                Update tax withholding
              </span>
            </div>
          ))
        )}
      </div>

      <div
        style={{
          background: BG_CARD,
          borderTop: "0.5px solid " + BORDER,
          padding: "16px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button onClick={goBack} style={BTN_GHOST}>
          Cancel
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span
            style={LINK}
            onClick={() => alert("Make location inactive coming next")}
          >
            Make inactive
          </span>
          <button onClick={goBack} style={BTN_TEAL}>
            Done
          </button>
        </div>
      </div>

      <AssignPanel
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        eligible={eligible}
      />
    </div>
  );
}